import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { FaPlus, FaTrash, FaEdit, FaNotesMedical, FaSyringe } from 'react-icons/fa';
import {
    Box, CircularProgress, Paper, Button, Tabs, Tab, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, IconButton, Typography
} from '@mui/material';

import { getZiyaretler, getAsilamalar, deleteVisit, deleteAsilama } from '../services/api';
import ZiyaretForm from './ZiyaretForm';
import AsilamaForm from './AsilamaForm';
import ConfirmationDialog from './ConfirmationDialog';

const VeterinerlikHizmetleri = ({ donemId, kumesId }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFormOpen, setFormOpen] = useState(false);
    const [itemToProcess, setItemToProcess] = useState(null);
    const [isConfirmOpen, setConfirmOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const fetchData = useCallback(async () => {
        if (!donemId) { setData([]); return; }
        setLoading(true);
        try {
            const response = activeTab === 0 ? await getZiyaretler(donemId) : await getAsilamalar(donemId);
            setData(response.data.data || []);
        } catch (err) {
            enqueueSnackbar(`Veriler alınamadı: ${err.message}`, { variant: 'error' });
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [donemId, activeTab, enqueueSnackbar]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleOpenForm = (item = null) => {
        setItemToProcess(item);
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setFormOpen(false);
        setItemToProcess(null);
    };

    const handleSaveSuccess = () => {
        fetchData();
        handleCloseForm();
    };

    const openDeleteDialog = (item) => {
        setItemToProcess(item);
        setConfirmOpen(true);
    };

    const closeDeleteDialog = () => {
        setConfirmOpen(false);
        setItemToProcess(null);
    };

    const handleDelete = async () => {
        if (!itemToProcess) return;
        setLoading(true);
        try {
            if (activeTab === 0) {
                await deleteVisit(itemToProcess.DONEM_VETERINERLIK_ID);
            } else {
                await deleteAsilama(itemToProcess.DONEM_ASILAMA_ID);
            }
            enqueueSnackbar('Kayıt başarıyla silindi.', { variant: 'success' });
            fetchData();
        } catch (err) {
            enqueueSnackbar(`Silme işlemi başarısız: ${err.message}`, { variant: 'error' });
        } finally {
            setLoading(false);
            closeDeleteDialog();
        }
    };

    const formatCurrency = (value) => value != null ? `${parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺` : '-';
    
    
    const formatDate = (dateInput) => {
        if (!dateInput) return '-';

        let date;

        
        if (typeof dateInput === 'object' && dateInput !== null && dateInput.year && dateInput.month && dateInput.day) {
            
            date = new Date(Date.UTC(dateInput.year, dateInput.month - 1, dateInput.day));
        }
        
        else if (typeof dateInput === 'string') {
            if (dateInput.includes('.')) {
                const parts = dateInput.split('.');
                if (parts.length === 3) {
                    const [day, month, year] = parts;
                    date = new Date(Date.UTC(year, month - 1, day));
                }
            } else {
                date = new Date(dateInput);
            }
        }
        
        else {
            date = new Date(dateInput);
        }
        
        
        if (!date || isNaN(date.getTime())) {
            return 'Geçersiz Tarih';
        }

        
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <Paper sx={{ p: 2, borderRadius: '16px', boxShadow: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ '& .MuiTabs-indicator': { backgroundColor: '#FF7A2B' }, '& .MuiTab-root.Mui-selected': { color: '#FF7A2B', fontWeight: 'bold' } }}>
                    <Tab icon={<FaNotesMedical />} iconPosition="start" label="Ziyaretler" />
                    <Tab icon={<FaSyringe />} iconPosition="start" label="Aşılama" />
                </Tabs>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => handleOpenForm()} disabled={!donemId} sx={{ m: 1, backgroundColor: '#FF7A2B', '&:hover': { backgroundColor: '#F57C00' } }}>
                    {activeTab === 0 ? 'Yeni Ziyaret' : 'Yeni Aşılama'}
                </Button>
            </Box>

            <TableContainer sx={{ mt: 2, maxHeight: 440 }}>
                {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress sx={{ color: '#FF7A2B' }} /></Box> : (
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{activeTab === 0 ? 'Veteriner' : 'Aşı Adı'}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tarih</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{activeTab === 0 ? 'Canlı Ağırlık (gr)' : 'Not'}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Hizmet Tutarı</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? data.map((item) => (
                                <TableRow key={item.DONEM_VETERINERLIK_ID || item.DONEM_ASILAMA_ID} hover>
                                    <TableCell>{item.VETERINERLIK_ADI || item.ASILAMA_ADI}</TableCell>
                                    <TableCell>{formatDate(item.TARIH || item.ZAMAN || item.ZAMAN_STR)}</TableCell>
                                    <TableCell>{activeTab === 0 ? (item.ZIYARET_GUN_AGIRLIK_GR || '-') : (item.VETERINER_NOTU || '-')}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.GENEL_TOPLAM)}</TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" sx={{ color: '#FF7A2B' }} onClick={() => handleOpenForm(item)} title="Düzenle"><FaEdit /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => openDeleteDialog(item)} title="Sil"><FaTrash /></IconButton>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow><TableCell colSpan={5} align="center"><Typography sx={{ p: 3, color: 'text.secondary' }}>Veri bulunamadı.</Typography></TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {isFormOpen && activeTab === 0 && (
                <ZiyaretForm open={isFormOpen} onClose={handleCloseForm} onSave={handleSaveSuccess} kumesId={kumesId} donemId={donemId} ziyaretData={itemToProcess} />
            )}
            
            {isFormOpen && activeTab === 1 && (
                <AsilamaForm open={isFormOpen} onClose={handleCloseForm} onSave={handleSaveSuccess} kumesId={kumesId} donemId={donemId} asilamaData={itemToProcess} />
            )}

            <ConfirmationDialog open={isConfirmOpen} onClose={closeDeleteDialog} onConfirm={handleDelete} title="Kaydı Sil" message="Bu kaydı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz." />
        </Paper>
    );
};

export default VeterinerlikHizmetleri;